import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCookieConsent } from '../context/CookieConsentContext';
import { getInitialLang } from '../legal/lang';

// Pages that must fit one screen (the landing) need to know how much room the
// banner is taking so it never covers their actions. Publishing the measured
// height as a CSS variable keeps that decoupled — and correct for any language,
// since the German copy wraps to a different height than the English.
const HEIGHT_VAR = '--glimt-consent-height';

const COPY = {
  en: {
    text: 'We use only strictly necessary storage to keep you signed in and remember your choices. We use no analytics or advertising cookies.',
    learn: 'Cookie Notice',
    accept: 'Accept',
    necessary: 'Only necessary',
  },
  de: {
    text: 'Wir verwenden ausschließlich unbedingt erforderlichen Speicher, um Sie angemeldet zu halten und Ihre Auswahl zu speichern. Wir setzen keine Analyse- oder Werbe-Cookies ein.',
    learn: 'Cookie-Hinweis',
    accept: 'Akzeptieren',
    necessary: 'Nur erforderliche',
  },
};

export default function CookieConsentBanner() {
  const { hasDecided, acceptAll, acceptNecessary } = useCookieConsent();
  const [lang] = useState(getInitialLang);
  const cardRef = useRef(null);

  useEffect(() => {
    const root = document.documentElement;
    if (hasDecided || !cardRef.current) {
      root.style.setProperty(HEIGHT_VAR, '0px');
      return undefined;
    }

    const el = cardRef.current;
    const publish = () => {
      // 16px is the banner's own bottom offset.
      root.style.setProperty(HEIGHT_VAR, `${Math.ceil(el.offsetHeight) + 16}px`);
    };
    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.setProperty(HEIGHT_VAR, '0px');
    };
  }, [hasDecided]);

  if (hasDecided) return null;
  const t = COPY[lang];

  return (
    <AnimatePresence>
      <motion.div
        ref={cardRef}
        role="dialog"
        aria-label={t.learn}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        style={{
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: 16,
          zIndex: 1000,
          maxWidth: 520,
          margin: '0 auto',
          background: '#FFFFFF',
          borderRadius: 18,
          boxShadow: '0 8px 30px rgba(64,42,20,0.18)',
          padding: 18,
        }}
      >
        <p style={{ fontFamily: 'Poppins', fontSize: 13.5, lineHeight: 1.55, color: '#5A4636', margin: 0 }}>
          {t.text}{' '}
          <Link to="/legal/cookies" style={{ color: '#44231E', textDecoration: 'underline', fontWeight: 500 }}>
            {t.learn}
          </Link>
        </p>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={acceptNecessary}
            style={{
              fontFamily: 'Poppins', fontSize: 14, fontWeight: 500,
              color: '#8E7C69', background: 'transparent',
              border: '1.5px solid #DCCFC0', borderRadius: 100,
              padding: '10px 18px', cursor: 'pointer',
            }}
          >
            {t.necessary}
          </button>
          <button
            type="button"
            onClick={acceptAll}
            style={{
              fontFamily: 'Poppins', fontSize: 14, fontWeight: 500,
              color: '#EFEBE0', background: '#44231E',
              border: 'none', borderRadius: 100,
              padding: '10px 22px', cursor: 'pointer',
            }}
          >
            {t.accept}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
